import type { ComponentType } from "react";
import type { FunctionReference } from "convex/server";
import { api } from "convex/_generated/api";
import { useTranslation } from "@/packages/utils";
import { BackupExportPanel } from "./BackupExportPanel";
import { BackupImportPanel } from "./BackupImportPanel";

interface ActionField {
  name: string;
  label: string;
  type: "text" | "user-select";
  placeholder: string;
}

export interface AdminActionConfig {
  id: string;
  group: string;
  label: string;
  description: string;
  dangerous: boolean;
  fields?: ActionField[];
  mutationRef?: FunctionReference<"mutation", "public">;
  customPanel?: ComponentType<{ onClose: () => void }>;
}

export interface AdminActionGroup {
  group: string;
  actions: AdminActionConfig[];
}

export function useAdminActions(): AdminActionGroup[] {
  const { t } = useTranslation(["admin"]);

  const actions: AdminActionConfig[] = [
    {
      id: "hard-delete-user",
      group: t("actions.groups.userManagement"),
      label: t("actions.hardDeleteUser.label"),
      description: t("actions.hardDeleteUser.description"),
      dangerous: true,
      fields: [
        {
          name: "userId",
          label: t("actions.hardDeleteUser.userId"),
          type: "user-select",
          placeholder: t("actions.hardDeleteUser.userIdPlaceholder"),
        },
      ],
      mutationRef: api.admin_actions.hardDeleteUser,
    },
    {
      id: "export-backup",
      group: t("actions.groups.backupRestore"),
      label: t("actions.exportBackup.label"),
      description: t("actions.exportBackup.description"),
      dangerous: false,
      customPanel: BackupExportPanel,
    },
    {
      id: "import-restore",
      group: t("actions.groups.backupRestore"),
      label: t("actions.importRestore.label"),
      description: t("actions.importRestore.description"),
      dangerous: true,
      customPanel: BackupImportPanel,
    },
  ];

  // Group actions by their group field, preserving order
  const groupMap = new Map<string, AdminActionConfig[]>();
  for (const action of actions) {
    const existing = groupMap.get(action.group);
    if (existing) {
      existing.push(action);
    } else {
      groupMap.set(action.group, [action]);
    }
  }

  return Array.from(groupMap.entries()).map(([group, groupActions]) => ({
    group,
    actions: groupActions,
  }));
}
